import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { trackSectionView } from '../utils/analytics';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { IconStrategy } from '../utils/PabsIcons';

const toolsAbove = [
  { name: 'Salesforce', category: 'CRM' },
  { name: 'Dovetail', category: 'Insights' },
  { name: 'Jira', category: 'Project' },
  { name: 'Confluence', category: 'Knowledge' },
  { name: 'Slack', category: 'Comms' },
  { name: 'Gong', category: 'Revenue' },
];

const toolsBelow = [
  { name: 'Codex', category: 'AI Coding Client' },
  { name: 'Ideally', category: 'AI User Research' },
  { name: 'Replit', category: 'AI Builder' },
  { name: 'GitHub Copilot', category: 'AI Assistant' },
  { name: 'Figma', category: 'AI Design' },
  { name: 'Claude', category: 'AI Agent' },
  { name: 'Monday', category: 'AI Agent' },
];

export default function MCPRevolutionSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            trackSectionView('mcp-revolution');
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="mcp-revolution"
      ref={sectionRef}
      className="pt-10 pb-32 bg-gradient-to-b from-brand-900 to-brand-800 relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:80px_80px]"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/50 to-transparent"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">

          {/* Section Header */}
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-7xl font-medium mb-8 leading-tight text-white">
              Keep your product vision alive as you build with AI
            </h2>
            <p className="text-xl text-brand-400 leading-relaxed font-light max-w-3xl mx-auto">
              Pabs coordinates product and design across your connected agents and tools, keeping every build aligned with your strategy as it evolves.
            </p>
          </div>

          {/* Orchestration Diagram */}
          <div className="relative">

            {/* Tools Above */}
            <div className="mb-12">
              <div className="flex items-center justify-center gap-3 mb-6">
                <ArrowDown className="w-5 h-5 text-brand-500 animate-bounce" />
                <span className="text-xs font-bold text-brand-500 uppercase tracking-wider">
                  The sources you choose
                </span>
                <ArrowUp className="w-5 h-5 text-brand-500 animate-bounce" />
              </div>

              <div className="max-w-6xl mx-auto border border-brand-50/10 rounded-[2rem] bg-brand-50/5 backdrop-blur-sm overflow-hidden">
                <div className="flex flex-wrap md:flex-nowrap divide-y md:divide-y-0 md:divide-x divide-brand-50/10">
                  {toolsAbove.map((tool, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: -10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="w-1/2 md:w-auto flex-1 group p-6 flex flex-col items-center justify-center hover:bg-brand-50/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-700 to-brand-900 mb-3 flex items-center justify-center text-white text-xs font-bold group-hover:scale-110 transition-transform shadow-lg shadow-black/20">
                        {tool.name.substring(0, 2)}
                      </div>
                      <div className="text-sm font-bold text-white mb-1 text-center">{tool.name}</div>
                      <div className="text-[10px] text-brand-500 uppercase tracking-wider text-center">
                        {tool.category}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            {/* Connecting Flow — top */}
            <div className="flex justify-center mb-8 h-12 relative overflow-hidden">
              <div className="absolute inset-0 flex justify-center gap-8 opacity-40">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`down-${i}`}
                    animate={{ y: [-50, 50], opacity: [0, 1, 0] }}
                    transition={{ duration: 2, delay: i * 0.5, repeat: Infinity, ease: 'linear' }}
                    className="w-0.5 h-12 bg-gradient-to-b from-transparent via-blue-400 to-transparent"
                  />
                ))}
              </div>
              <div className="absolute inset-0 flex justify-center gap-8 opacity-40 translate-x-4">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`up-${i}`}
                    animate={{ y: [50, -50], opacity: [0, 1, 0] }}
                    transition={{ duration: 2.5, delay: i * 0.7, repeat: Infinity, ease: 'linear' }}
                    className="w-0.5 h-12 bg-gradient-to-t from-transparent via-white to-transparent"
                  />
                ))}
              </div>
            </div>

            {/* Pabs Strategic Layer */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="relative mb-8"
            >
              <div className="max-w-2xl mx-auto p-12 rounded-[3rem] bg-gradient-to-br from-brand-50/10 to-brand-50/5 border-2 border-brand-50/20 backdrop-blur-xl shadow-2xl relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-brand-50/5 to-transparent"></div>

                <div className="relative z-10 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white to-brand-200 mb-6 flex items-center justify-center mx-auto shadow-2xl">
                    <IconStrategy className="w-8 h-8 text-brand-900" />
                  </div>
                  <h3 className="text-3xl font-medium text-white mb-3">Pabs product and design authority</h3>
                  <p className="text-brand-400 leading-relaxed max-w-md mx-auto">
                    What you’re building, how it should work and why you chose it.
                  </p>

                  <div className="flex flex-wrap justify-center gap-3 mt-8">
                    {['Decisions', 'Design Authority', 'Jobs', 'Learnings'].map(
                      (pill) => (
                        <div
                          key={pill}
                          className="px-4 py-2 rounded-full bg-brand-50/10 border border-brand-50/20 text-xs font-bold text-white backdrop-blur-sm"
                        >
                          {pill}
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-r from-brand-50/20 via-transparent to-brand-50/20 blur-xl opacity-50 animate-pulse"></div>
              </div>
            </motion.div>

            {/* Connecting Flow — bottom */}
            <div className="flex justify-center mb-8 h-12 relative overflow-hidden">
              <div className="absolute inset-0 flex justify-center gap-8 opacity-40">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`down-bot-${i}`}
                    animate={{ y: [-50, 50], opacity: [0, 1, 0] }}
                    transition={{ duration: 1.8, delay: i * 0.4, repeat: Infinity, ease: 'linear' }}
                    className="w-0.5 h-12 bg-gradient-to-b from-transparent via-blue-400 to-transparent"
                  />
                ))}
              </div>
              <div className="absolute inset-0 flex justify-center gap-8 opacity-40 translate-x-4">
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`up-bot-${i}`}
                    animate={{ y: [50, -50], opacity: [0, 1, 0] }}
                    transition={{ duration: 2.2, delay: i * 0.6, repeat: Infinity, ease: 'linear' }}
                    className="w-0.5 h-12 bg-gradient-to-t from-transparent via-white to-transparent"
                  />
                ))}
              </div>
            </div>

            {/* Tools Below */}
            <div>
              <div className="flex items-center justify-center gap-3 mb-6">
                <ArrowDown className="w-5 h-5 text-brand-500 animate-bounce" />
                <span className="text-xs font-bold text-brand-500 uppercase tracking-wider">
                  The tools you build with
                </span>
                <ArrowUp className="w-5 h-5 text-brand-500 animate-bounce" />
              </div>

              <div className="max-w-6xl mx-auto border border-brand-50/10 rounded-[2rem] bg-brand-50/5 backdrop-blur-sm overflow-hidden">
                <div className="flex flex-wrap md:flex-nowrap divide-y md:divide-y-0 md:divide-x divide-brand-50/10">
                  {toolsBelow.map((tool, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="w-1/2 md:w-auto flex-1 group p-6 flex flex-col items-center justify-center hover:bg-brand-50/10 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-900 to-black mb-3 flex items-center justify-center text-white text-xs font-bold group-hover:scale-110 transition-transform shadow-lg shadow-black/20">
                        {tool.name.substring(0, 2)}
                      </div>
                      <div className="text-sm font-bold text-white mb-1 text-center">{tool.name}</div>
                      <div className="text-[10px] text-brand-500 uppercase tracking-wider text-center">
                        {tool.category}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Explainer */}
          <div className="mt-20 text-center">
            <p className="text-brand-400 leading-relaxed max-w-3xl mx-auto text-lg">
              Decisions guide the work. Jobs follow it through. Design Authority checks the result.
              Learnings feed the next choice.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-brand-400 max-w-3xl mx-auto">You choose which sources to connect and what Pabs should review. Regular checks run when you schedule them.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
